import React, { useEffect, useRef } from 'react';
import '../styles/HomePage.css';

const HomePage = () => {
  // 비디오 참조를 위한 ref 생성
  const video1Ref = useRef(null);
  
  // 비디오 재생 가능 이벤트 핸들러 (스트리밍 시작)
  const handleVideoCanPlay = (event) => {
    event.target.classList.add('can-play');
    // 영상이 재생 가능하면 즉시 root의 initial-loading 클래스 제거
    const root = document.getElementById('root');
    if (root && root.classList.contains('initial-loading')) {
      root.classList.remove('initial-loading');
    }
  };

  // 비디오 완전 로드 이벤트 핸들러 (백그라운드에서 완료)
  const handleVideoLoadComplete = (event) => {
    event.target.classList.add('fully-loaded');
  };

  useEffect(() => {
    // 1. 먼저 기존 fullPage.js 인스턴스 정리
    if (window.$ && window.$.fn && window.$.fn.fullpage && window.$.fn.fullpage.destroy) {
      window.$.fn.fullpage.destroy('all');
    }
    
    // 2. fullPage.js 관련 클래스들 정리
    document.body.classList.remove('fp-enabled', 'fp-responsive', 'fp-viewing-0', 'fp-viewing-section1');
    document.documentElement.classList.remove('fp-enabled', 'fp-responsive');
    
    // 3. body에 fullpage-enabled 클래스 추가 (CSS에서 overflow: hidden 적용)
    document.body.classList.add('fullpage-enabled');
    
    // 4. root에 initial-loading 클래스 추가
    const root = document.getElementById('root');
    if (root) {
      root.classList.add('initial-loading');
    }
    
    // 5. 약간의 지연 후 fullPage.js 초기화 (DOM이 완전히 준비되도록)
    const initTimer = setTimeout(() => {
      if (typeof window.initFullPage === 'function') {
        window.initFullPage();
      }
    }, 50);
    
    // 언마운트 시 정리
    return () => {
      clearTimeout(initTimer);
      
      // fullPage.js 인스턴스 제거
      if (window.$ && window.$.fn && window.$.fn.fullpage && window.$.fn.fullpage.destroy) {
        window.$.fn.fullpage.destroy('all');
      }
      
      // 클래스 제거
      document.body.classList.remove('fullpage-enabled');
      document.body.classList.remove('fp-enabled', 'fp-responsive', 'fp-viewing-0', 'fp-viewing-section1');
      document.documentElement.classList.remove('fp-enabled', 'fp-responsive');
    };
  }, []);

  return (
    <div id="fullpage">
      {/* 섹션 1: 병원 내부 영상 (배경) - 개선된 애니메이션 */}
      <div className="section section-1">
        <div className="video-placeholder"></div>
        <video
          ref={video1Ref}
          className="fullscreen-bg-video"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          // poster="/assets/메인-섹션2배경.jpg"
          onCanPlay={handleVideoCanPlay}
          onLoadedData={handleVideoLoadComplete}
        >
          <source src="/assets/메인-섹션1배경v2.mp4" type="video/mp4" />
          브라우저가 비디오를 지원하지 않습니다.
        </video>
        <div className="content-wrapper center-content">
          <div className="logo-wrapper logo-glow">
            <img 
              src="/assets/한앤손부부치과_로고.webp" 
              alt="한앤손 부부치과 로고" 
              className="home-logo"
            />
          </div>
          <h1 className="main-title title-magic">한앤손 부부치과</h1>
          <p className="subtitle subtitle-float">정확한 진단과 섬세한 치료로 함께합니다</p>
        </div>
      </div>

      {/* 섹션 2: 마음을 공감합니다 - 좌측 정렬 디자인 */}
      <div className="section section-2">
        <div className="video-placeholder"></div>
        <div 
          className="fullscreen-bg-image"
          style={{ backgroundImage: 'url(/assets/메인-섹션2배경.webp)' }}
        ></div>
        <div className="content-wrapper left-content">
          <div className="text-container">
            <h2 className="section-title slide-from-left">마음을 공감합니다</h2>
            <div className="text-content">
              <p className="text-wave-1">치료의 시작은 공감입니다.</p>
              <p className="text-wave-2">아플 때의 고통, 그럼에도 병원에 가고 싶지 않은 마음,</p>
              <p className="text-wave-3">그리고 내가, 내 가족이 병원에 갔을 때 만나고 싶은 의료진과 치료,</p>
              <p className="text-wave-4">그 마음을 공감할 때 좋은 진료가 시작됩니다.</p>
              <p className="text-wave-5">한앤손부부치과의 모든 의료진은 공감과 소통의 열린 마음으로</p>
              <p className="text-wave-6">환자와의 첫 만남을 시작합니다.</p>
            </div>
          </div>
        </div>
      </div>

      {/* 섹션 3: 정확한 진단과 섬세한 치료 - 우측 정렬 디자인 */}
      <div className="section section-3">
        <div className="video-placeholder"></div>
        <div 
          className="fullscreen-bg-image" 
          style={{ backgroundImage: 'url(/assets/메인-섹션3배경.webp)' }}
        ></div>
        <div className="content-wrapper right-content">
          <div className="text-container">
            <h2 className="section-title slide-from-right">정확한 진단, 그리고<br className="mobile-break" /> 꼭 필요한 치료만을 약속합니다.</h2>
            <div className="text-content">
              <p className="slide-from-right-delay-1">한앤손부부치과는 풍부한 임상 경험과 첨단 장비로</p>
              <p className="slide-from-right-delay-2">치아 하나, 잇몸 한 곳도 놓치지 않고 꼼꼼하게 진단합니다.</p>
              <p className="slide-from-right-delay-3">불필요한 과잉진료 없이,</p>
              <p className="slide-from-right-delay-4">환자에게 꼭 필요한 치료만을 신중하고 정직하게 제공합니다.</p>
              <p className="slide-from-right-delay-5">환자의 일상과 마음까지 세심하게 배려하는 진료,</p>
              <p className="slide-from-right-delay-6">한앤손부부치과가 지키는 원칙입니다.</p>
            </div>
          </div>
        </div>
      </div>

      {/* 섹션 4: 주치의 - 중앙 정렬 디자인 */}
      <div className="section section-4">
        <div className="video-placeholder"></div>
        <div 
          className="fullscreen-bg-image" 
          style={{ backgroundImage: 'url(/assets/메인-섹션4배경.webp)' }}
        ></div>
        <div className="content-wrapper center-content">
          <div className="text-container">
            <h2 className="section-title zoom-in">우리동네 우리가족 치과 주치의</h2>
            <div className="text-content">
              <p className="fade-in-zoom-delay-1">한 번 맺은 인연, 오랫동안 지켜드립니다.</p>
              <p className="fade-in-zoom-delay-2">진료의 시작부터 마무리까지</p>
              <p className="fade-in-zoom-delay-3">한앤손부부치과 부부 의료진이</p>
              <p className="fade-in-zoom-delay-4">언제나 변함없이 함께합니다.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage; 